/**
 * GHG inventory rows + population for Analysis rankings (cities and counties).
 */

import {
  canonicalInventoryGeofipsKey,
  inventoryGeofipsNumeric,
  preferNewerPrecalcRow,
} from './ghgInventoryGeofipsCanonical';
import { supabaseClient } from './supabase';
import {
  sectorMtForMetric,
  type CountyGhgMapMetric,
  type CountyGhgMapRow,
} from './countyPerCapitaGhgMapService';

export type { CountyGhgMapMetric };

const PAGE_SIZE = 1000;

export type GhgRankingGeography = 'City' | 'County';

export interface GhgRankingPlaceRow {
  geofips: number;
  location_name: string;
  geography_type: GhgRankingGeography;
  population: number;
  /** Wastewater-inclusive total; equals legacy total when wastewater is null. */
  total_mt: number;
  ldv_mt: number;
  hdv_mt: number;
  elec_mt: number;
  ng_mt: number;
  ag_mt: number;
  offroad_mt: number;
  waste_mt: number;
  /** Wastewater MTCO₂e from precalc; null = not yet calculated. */
  wastewater_mt: number | null;
}

function num(v: unknown): number {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function toMapRow(r: GhgRankingPlaceRow): CountyGhgMapRow {
  return {
    geofips: r.geofips,
    countyFp3: '',
    location_name: r.location_name,
    population: r.population,
    total_mt: r.total_mt, // already wastewater-inclusive via COALESCE
    ldv_mt: r.ldv_mt,
    hdv_mt: r.hdv_mt,
    elec_mt: r.elec_mt,
    ng_mt: r.ng_mt,
    ag_mt: r.ag_mt,
    offroad_mt: r.offroad_mt,
    waste_mt: r.waste_mt,
  };
}

export function perCapitaMtForRankingRow(r: GhgRankingPlaceRow, metric: CountyGhgMapMetric): number | null {
  if (r.population <= 0) return null;
  const pc = sectorMtForMetric(toMapRow(r), metric) / r.population;
  return Number.isFinite(pc) ? pc : null;
}

export function totalMtForRankingRow(r: GhgRankingPlaceRow, metric: CountyGhgMapMetric): number {
  return sectorMtForMetric(toMapRow(r), metric);
}

export interface GhgRankingsPayload {
  year: number;
  geographyType: GhgRankingGeography;
  rows: GhgRankingPlaceRow[];
  error: string | null;
}

export async function fetchGhgRankingPlaces(
  year: number,
  geographyType: GhgRankingGeography
): Promise<GhgRankingsPayload> {
  type RawGhg = {
    geofips: number;
    geofipsRaw: unknown;
    location_name: string;
    calculation_date: string | null;
    total_emissions: unknown;
    total_emissions_with_wastewater: unknown;
    ldv_emissions: unknown;
    hdv_emissions: unknown;
    elec_total_emissions: unknown;
    ng_total_emissions: unknown;
    waste_emissions: unknown;
    offroad_emissions: unknown;
    agriculture_emissions: unknown;
    wastewater_emissions: unknown;
  };

  const raw: RawGhg[] = [];
  let from = 0;
  for (;;) {
    let q = supabaseClient
      .from('ghg_inventory_precalculated')
      .select(
        'geofips,location_name,calculation_date,total_emissions,total_emissions_with_wastewater,ldv_emissions,hdv_emissions,elec_total_emissions,ng_total_emissions,waste_emissions,offroad_emissions,agriculture_emissions,wastewater_emissions'
      )
      .eq('year', year)
      .eq('geography_type', geographyType)
      .order('geofips', { ascending: true });

    if (geographyType === 'County') {
      q = q.gte('geofips', 6000).lt('geofips', 7000);
    } else {
      q = q.gte('geofips', 600000).lt('geofips', 700000);
    }

    const { data, error } = await q.range(from, from + PAGE_SIZE - 1);
    if (error) {
      return { year, geographyType, rows: [], error: error.message };
    }
    const batch = (data ?? []) as Record<string, unknown>[];
    if (batch.length === 0) break;
    for (const r of batch) {
      const gf = inventoryGeofipsNumeric(r.geofips);
      if (gf == null) continue;
      raw.push({
        geofips: gf,
        geofipsRaw: r.geofips,
        location_name: String(r.location_name || '').trim(),
        calculation_date: r.calculation_date != null ? String(r.calculation_date) : null,
        total_emissions: r.total_emissions,
        total_emissions_with_wastewater: r.total_emissions_with_wastewater,
        ldv_emissions: r.ldv_emissions,
        hdv_emissions: r.hdv_emissions,
        elec_total_emissions: r.elec_total_emissions,
        ng_total_emissions: r.ng_total_emissions,
        waste_emissions: r.waste_emissions,
        offroad_emissions: r.offroad_emissions,
        agriculture_emissions: r.agriculture_emissions,
        wastewater_emissions: r.wastewater_emissions,
      });
    }
    if (batch.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  const merged = new Map<string, RawGhg>();
  for (const r of raw) {
    const name = r.location_name.toLowerCase();
    if (!r.location_name || name === 'california') continue;
    const canon = canonicalInventoryGeofipsKey(r.geofipsRaw);
    if (!canon) continue;
    const prev = merged.get(canon);
    merged.set(canon, prev ? preferNewerPrecalcRow(prev, r) : r);
  }

  const byGeofips = new Map<number, RawGhg>();
  for (const r of Array.from(merged.values())) {
    const gf = inventoryGeofipsNumeric(r.geofipsRaw) ?? r.geofips;
    byGeofips.set(gf, { ...r, geofips: gf });
  }

  const popMap = new Map<number, number>();
  from = 0;
  for (;;) {
    let pq = supabaseClient
      .from('population')
      .select('geofips,population')
      .eq('year', year)
      .eq('geography_type', geographyType);

    if (geographyType === 'County') {
      pq = pq.gte('geofips', 6000).lt('geofips', 7000);
    } else {
      pq = pq.gte('geofips', 600000).lt('geofips', 700000);
    }

    const { data, error } = await pq.range(from, from + PAGE_SIZE - 1);
    if (error) {
      return { year, geographyType, rows: [], error: error.message };
    }
    const batch = (data ?? []) as Record<string, unknown>[];
    if (batch.length === 0) break;
    for (const r of batch) {
      const gf = inventoryGeofipsNumeric(r.geofips);
      const p = num(r.population);
      if (gf != null && p > 0) popMap.set(gf, p);
    }
    if (batch.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  const rows: GhgRankingPlaceRow[] = [];
  for (const g of Array.from(byGeofips.values())) {
    const wwRaw = g.wastewater_emissions != null ? Number(g.wastewater_emissions) : null;
    const wastewater_mt = wwRaw != null && Number.isFinite(wwRaw) ? wwRaw : null;
    // Use wastewater-inclusive total when available; fall back to legacy total
    const totalWithWw = g.total_emissions_with_wastewater != null
      ? num(g.total_emissions_with_wastewater)
      : num(g.total_emissions);
    rows.push({
      geofips: g.geofips,
      location_name: g.location_name,
      geography_type: geographyType,
      population: popMap.get(g.geofips) ?? 0,
      total_mt: totalWithWw,
      ldv_mt: num(g.ldv_emissions),
      hdv_mt: num(g.hdv_emissions),
      elec_mt: num(g.elec_total_emissions),
      ng_mt: num(g.ng_total_emissions),
      ag_mt: num(g.agriculture_emissions),
      offroad_mt: num(g.offroad_emissions),
      waste_mt: num(g.waste_emissions),
      wastewater_mt,
    });
  }

  rows.sort((a, b) => a.geofips - b.geofips);
  return { year, geographyType, rows, error: null };
}
